import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories } from "../contexts";
import { getRequestOptions } from "../helper-functions";
import { useAxios, useGenericQuery } from "../hooks";
import {
    ChartReviewLog,
    Entity,
    McrDashboardData,
    PagedResponse,
    QaDashboardData,
    QueryParams,
    QueryResult,
} from "../models";

interface IUseDonorQuery {
    mcrDashboardQuery: QueryResult<McrDashboardData>;
    qaDashboardQuery: QueryResult<QaDashboardData>;
    chartReviewLogsQuery: QueryResult<PagedResponse<ChartReviewLog>>;
}

interface IUseDonorQueryParams {
    queryType: "mcr-dashboard" | "qa-dashboard" | "chart-review-logs";
}

export const useDonorQuery = ({ queryType }: IUseDonorQueryParams): IUseDonorQuery => {
    const { isAuthorized } = useAuth();
    const { get } = useAxios();
    const { selectedTerritoryIds } = useTerritories();
    const {
        queryParams: queryParamsState,
        sortParams,
        pageParams,
    } = useQueryParams().getParamsApi(Entity.ChartReviewLogs);
    const url = Endpoints.Donors;

    const queryParams: QueryParams = {
        ...queryParamsState,
        territoryIds: selectedTerritoryIds.join(","),
    };

    const mcrDashboardQuery = useGenericQuery({
        queryFn: (config) => get<McrDashboardData>(getRequestOptions(`${url}/mcr-dashboard`, config, queryParams)),
        queryKey: queryKeys.dashboards.mcr(queryParams),
        placeholderData: {} as McrDashboardData,
        enabled: isAuthorized && queryType === "mcr-dashboard",
        staleTime: 5000,
        cacheTime: 5000,
    });

    const qaDashboardQuery = useGenericQuery({
        queryFn: (config) => get<QaDashboardData>(getRequestOptions(`${url}/qa-dashboard`, config, queryParams)),
        queryKey: queryKeys.dashboards.qa(queryParams),
        placeholderData: {} as QaDashboardData,
        enabled: isAuthorized && queryType === "qa-dashboard",
        staleTime: 5000,
        cacheTime: 5000,
    });

    const chartReviewLogsQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<ChartReviewLog>>(
                getRequestOptions(`${url}/review-logs`, config, queryParams, sortParams, pageParams)
            ),
        queryKey: queryKeys.donors.chartReviewLogs({ ...queryParams, ...sortParams, ...pageParams }),
        placeholderData: {} as PagedResponse<ChartReviewLog>,
        enabled: isAuthorized && queryType === "chart-review-logs",
        staleTime: 5000,
        cacheTime: 5000,
    });

    return useMemo<IUseDonorQuery>(
        () => ({
            mcrDashboardQuery,
            qaDashboardQuery,
            chartReviewLogsQuery,
        }),
        [mcrDashboardQuery, qaDashboardQuery, chartReviewLogsQuery]
    );
};
